/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable max-len */
import { Form, Row } from 'antd';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData, ILeiMatka, ILeiSubory } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { IcisStaty } from 'services/cisStaty';
import fileDownload from 'services/fileDownload';
import checkLeiApi from 'services/lei/checkLEI';
import prepareFile from 'services/lei/fileDownload';

import { EAnoNe } from 'enums/anoNe';
import { EPeriodType, ETypDatMatky, ETypMatky, ETypZadostiLei } from 'enums/lei/leiDataEnums';
import { EMatNemaDuvod } from 'enums/lei/matNemaDuvod';
import { EMaterskaSpol } from 'enums/lei/materskaSpol';
import { ENotificationType } from 'enums/notificationType';

import anoNe from 'data/codebooks/anoNeSelect';
import { leiKonsolidaceSouborySelect } from 'data/codebooks/lei/leiKonsolidaceSouborySelect';
import {
    leiKonsolidaceTypSelect,
    leiKonsolidaceTypSelectAlt,
    leiKonsolidaceTypSelectAltGov
} from 'data/codebooks/lei/leiKonsolidaceTypSelect';
import { leiMaMaterskouSpolFinalSelect } from 'data/codebooks/lei/leiMaMaterskouSpolSelect';
import { leiMatNelzeDuvodSelect } from 'data/codebooks/lei/leiMatNelzeDuvodSelect';
import { leiMatNemaDuvodSelect } from 'data/codebooks/lei/leiMatNemaDuvodSelect';
import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';
import leiRejstrikValidation from 'utils/validations/leiRejstrikValidation';
import leiValidation from 'utils/validations/leiValidation';
import { procentaValidation } from 'utils/validations/procentaValidation';

interface IFinalniMatka {
    prevStep: () => void;
    setFinalniMatSubmitdata: React.Dispatch<React.SetStateAction<ILeiMatka | undefined>>;
    setFormData: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    showFinalniMatka: boolean;
    finalniMatkaData?: ILeiMatka[];
    primaMatkaForm?: FormInstance;
    disabled?: boolean;
    primaMatkaFilelist?: IFileList[] | undefined;
    cisStaty: IcisStaty[];
    typZadosti?: string;
    rerenderVal: boolean;
    primaMatkaPrilozeneSoubory?: ILeiSubory[];
    entityCheckErr?: string;
    isEdit?: boolean;
    errorLei?: string;
    setErrorLei: React.Dispatch<React.SetStateAction<string>>;
    setEntityCheckErr: React.Dispatch<React.SetStateAction<'' | 'shodneico' | 'existuje'>>;
    loading?: boolean;
    setShowFinalniMatka?: React.Dispatch<React.SetStateAction<boolean>>;
}

const FinalniMatka: React.FC<IFinalniMatka> = ({
    setFinalniMatSubmitdata,
    setFormData,
    showFinalniMatka,
    finalniMatkaData,
    prevStep,
    primaMatkaForm,
    disabled,
    rerenderVal,
    primaMatkaFilelist,
    typZadosti,
    primaMatkaPrilozeneSoubory,
    entityCheckErr,
    loading,
    errorLei,
    setErrorLei,
    setEntityCheckErr,
    cisStaty,
    isEdit,
    setShowFinalniMatka
}) => {
    const { lei, NotificationComponent, language } = useContext(Context);
    const [form] = Form.useForm();
    const isData = finalniMatkaData && finalniMatkaData.length > 0 ? finalniMatkaData : undefined;
    const [leiCheckLoading, setLeiCheckLoading] = useState<boolean>(false);
    const [disableDownload, setDisableDownload] = useState<boolean>(false);
    const [hideDefaultValues, setHideDefaultValues] = useState<boolean>(false);
    const [showDefaultValues, setShowDefaultValues] = useState<{ registrationauthorityid: boolean }>({
        registrationauthorityid: true
    });
    const [leiCheckError, setLeiCheckError] = useState<string>('');
    const [selectedMatkaSame, setSelectedMatkaSame] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);

    const [rerenderValidate, setRerenderValidate] = useState<boolean>(false);
    const setForceRerender = useState<boolean>(false)[1];
    const rerender = (validateAfter?: boolean) => {
        if (validateAfter) {
            setRerenderValidate(true);
        } else {
            setForceRerender(val => !val);
        }
    };

    useEffect(() => {
        const stav = form.getFieldValue('stav');
        hideDefaultValues && form.resetFields();
        form.setFieldsValue({
            stav: stav
        });
        rerender();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hideDefaultValues]);

    useEffect(() => {
        setTimeout(() => {
            form.setFieldsValue({
                duvod: primaMatkaForm?.getFieldValue('duvod')
            });
            rerender();
        }, 200);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerenderVal]);

    useEffect(() => {
        if (rerenderValidate) {
            form.validateFields(['registrationauthorityentityid']);
            setRerenderValidate(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerenderValidate]);

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue({
            // duvod: isData && isData[0].duvod,
            malei: isData ? (isData[0].matkaslei ? 'ano' : isData[0].matkabezlei && 'ne') : undefined,
            validationdocuments: isData && isData[0].informacekmatce?.validationdocuments,
            jurisdikce: isData && isData[0].matkabezlei?.jurisdikce,
            registrationauthorityid: isData && isData[0].matkabezlei?.registrationauthorityid
        });
        setTimeout(() => rerender(), 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ILeiSubory[]>(
        isData && isData[0].informacekmatce?.soubory ? isData[0].informacekmatce.soubory : []
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        (isData && isData[0].informacekmatce?.soubory ? isData[0].informacekmatce.soubory : []).map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );

    const finalniMatkaDates = {
        accounting:
            isData &&
            isData[0].informacekmatce &&
            isData[0].informacekmatce.relationshipperiod?.filter(
                e => e.periodtype === EPeriodType.ACCOUNTING_PERIOD
            )[0],
        document:
            isData &&
            isData[0].informacekmatce &&
            isData[0].informacekmatce.relationshipperiod?.filter(
                e => e.periodtype === EPeriodType.DOCUMENT_FILING_PERIOD
            )[0],
        relationship:
            isData &&
            isData[0].informacekmatce &&
            isData[0].informacekmatce.relationshipperiod?.filter(
                e => e.periodtype === EPeriodType.RELATIONSHIP_PERIOD
            )[0]
    };

    useEffect(() => {
        // isData && isData[0].matkaslei && checkLei(isData[0].matkaslei);
        isData && isData[0].stav === EMaterskaSpol.ANO_SAME && setSelectedMatkaSame(true);
        form.setFieldsValue({
            relationship: finalniMatkaDates.relationship?.startdate
                ? dayjs(finalniMatkaDates.relationship?.startdate)
                : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* const hqLangValues = finalniMatkaData?.length
        ? finalniMatkaData[0].matkabezlei?.adresy &&
          finalniMatkaData[0].matkabezlei.adresy.filter(e => e.typadresy === ETypAdresy.HEADQUARTERSADDRES)
        : undefined;
*/

    /*
stav
duvod
matkaslei
   matkabezlei: {
                    jurisdikce
                    registrationauthorityid
                    ...(fieldsValue.otherregistrationauthorityid && {
                        otherregistrationauthorityid: fieldsValue.otherregistrationauthorityid
                    }),
                    registrationauthorityentityid: fieldsValue.registrationauthorityentityid
                }
*/
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any, saveOnly?: ILeiSubory[]) => {
        const formDataMatkaObj: ILeiMatka = {
            typmatky: ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY,
            // pokud nastavím totožné hodnoty přímé i finální, posílám stav 'Totozna' - interně se ale stále pracuje s jinou hodnotou!
            stav: primaMatkaForm?.getFieldValue('stav') === selectedMatkaSame ? 'Totozna' : fieldsValue.stav,
            ...(fieldsValue.duvod && {
                duvod: fieldsValue.duvod
            }),
            ...(fieldsValue.exceptionreference && {
                exceptionreference: fieldsValue.exceptionreference
            }),
            ...(fieldsValue.matkaslei && {
                matkaslei: fieldsValue.matkaslei
            }),
            ...(fieldsValue.malei === EAnoNe.NE && {
                matkabezlei: {
                    jurisdikce: fieldsValue.jurisdikce,
                    registrationauthorityid: fieldsValue.registrationauthorityid,
                    ...(fieldsValue.otherregistrationauthorityid && {
                        otherregistrationauthorityid: fieldsValue.otherregistrationauthorityid
                    }),
                    registrationauthorityentityid: fieldsValue.registrationauthorityentityid
                }
            }),
            ...(fieldsValue.malei &&
                fieldsValue.malei !== EAnoNe.NE && {
                    informacekmatce: {
                        ...(fieldsValue.relationship && {
                            datumodkdyplatnost:
                                isEdit && isData?.length && isData[0].informacekmatce?.datumodkdyplatnost
                                    ? isData[0].informacekmatce?.datumodkdyplatnost
                                    : dayjs().format('YYYY-MM-DD')
                        }),
                        konsolidace: fieldsValue.konsolidace,
                        podilspolecnosti: fieldsValue.podilspolecnosti,
                        soubory: saveOnly || prilozeneSoubory,
                        validationdocuments: fieldsValue.validationdocuments,
                        ...(fieldsValue.validationreference && {
                            validationreference: fieldsValue.validationreference
                        }),
                        relationshipperiod: [
                            ...(fieldsValue.accountingstart
                                ? [
                                      {
                                          periodtype: EPeriodType.ACCOUNTING_PERIOD,
                                          startdate: fieldsValue.accountingstart.format('YYYY-MM-DD'),
                                          ...(fieldsValue.accountingend && {
                                              enddate: fieldsValue.accountingend.format('YYYY-MM-DD')
                                          })
                                      }
                                  ]
                                : []),
                            ...(fieldsValue.documentstart
                                ? [
                                      {
                                          periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                          startdate: fieldsValue.documentstart.format('YYYY-MM-DD'),
                                          ...(fieldsValue.documentend && {
                                              enddate: fieldsValue.documentend.format('YYYY-MM-DD')
                                          })
                                      }
                                  ]
                                : []),
                            ...(fieldsValue.relationship
                                ? [
                                      {
                                          periodtype: EPeriodType.RELATIONSHIP_PERIOD,
                                          startdate: fieldsValue.relationship.format('YYYY-MM-DD')
                                      }
                                  ]
                                : [])
                        ]
                    }
                })
        };
        saveOnly &&
            setFormData(pv => {
                // ulozenie dat pri zmazani/uploade suobru
                return {
                    ...pv,
                    matky: [
                        ...(pv && pv.matky && pv.matky.length > 0
                            ? pv.matky.filter(
                                  e =>
                                      e.typmatky !== ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY &&
                                      e.typmatky !== ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                              )
                            : []),
                        ...(pv &&
                        pv.matky &&
                        pv.matky.length > 0 &&
                        pv.matky.filter(e => e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY).length > 0 &&
                        primaMatkaForm?.getFieldsValue().duvod !== EMatNemaDuvod.NON_PUBLIC
                            ? pv.matky.filter(e => e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY)
                            : [
                                  {
                                      typmatky: ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY,
                                      stav: primaMatkaForm?.getFieldsValue().stav,
                                      ...(primaMatkaForm?.getFieldsValue().duvod && {
                                          duvod: primaMatkaForm?.getFieldsValue().duvod
                                      })
                                  }
                              ]),

                        formDataMatkaObj
                    ]
                };
            });
        !saveOnly && setLeiCheckError('');
        !saveOnly && setFinalniMatSubmitdata(!disabled ? formDataMatkaObj : {});
    };

    const uploadProps = {
        showDownloadIcon: true,
        ...(isEdit && {
            extramessage: disableDownload ? 'LEI_UPLOAD_FILLIN' : '',
            onButtonClick: () => {
                form.validateFields(
                    Object.keys(form.getFieldsValue()).filter((o: any) => !String(o).includes('soubor'))
                )
                    .then(() => setDisableDownload(false))
                    .catch(() => setDisableDownload(true));
            },
            beforeUpload: (file: UploadFile) => {
                // kontrola maximalnej velkosti s suboru
                if ((file.size || 0) / 1024 / 1024 > 20) {
                    NotificationComponent({
                        title: 'UPLOAD_MAX_SIZE',
                        description: 'UPLOAD_MAX_SIZE_TEXT',
                        type: ENotificationType.ERROR
                    });
                    return false;
                }
                if (disableDownload) return false;
                return true;
            }
        }),
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    form.validateFields(['soubor']);
                    const prilozeneSouboryNew = data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                orignazev: e.name.normalize(),
                                tempnazev: e.response ? e.response.tempnazev : e.fileName
                            };
                        });
                    setPrilozeneSoubory(prilozeneSouboryNew);
                    onFinish(form.getFieldsValue(), prilozeneSouboryNew);
                }
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
                onFinish(form.getFieldsValue(), newSoubory);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFile(file.fileName ? file.fileName : '', lei?.idVarsymZadosti?.varsym || '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    return (
        <>
            <Paragraph
                text="LEI_FINAL_TEXT"
                style={{
                    display: 'block',
                    marginBottom: 16
                }}
            />
            {language === 'cz' && (
                <>
                    <Paragraph
                        text="LEI_DETAIL_MAT_TEXT2"
                        style={{
                            display: 'block'
                        }}
                    />
                    <Paragraph
                        text="LEI_DETAIL_MAT_TEXT3"
                        style={{
                            display: 'block'
                        }}
                    />
                </>
            )}
            {language === 'en' && (
                <Paragraph
                    text="LEI_FINAL_TEXT2"
                    style={{
                        display: 'block',
                        marginBottom: 10
                    }}
                />
            )}
            {/* language === 'cz' && (
                <Paragraph
                    text="LEI_FINAL_SUBTEXT"
                    style={{
                        display: 'block',
                        paddingBottom: 30
                    }}
                />
                )*/}
            <FormWrapper outForm={form} onFinish={onFinish} scrollToFirstError={false}>
                <SelectComponent
                    isFormItem={true}
                    name="stav"
                    defaultValue={
                        disabled || typZadosti === 'SOLE_PROPRIETOR'
                            ? EMaterskaSpol.NE
                            : !hideDefaultValues
                              ? isData
                                  ? isData[0].stav
                                  : undefined
                              : undefined
                    }
                    labelParagraph="LEI_FINAL_MASPOL_SELECT"
                    data={
                        disabled
                            ? [
                                  {
                                      labelcz: 'LEI_MATKA_KONSOLIDOVANA_NEUVEST',
                                      value: EMaterskaSpol.NE
                                  }
                              ]
                            : [
                                  {
                                      labelcz: 'LEI_MATKA_KONSOLIDOVANA_UVEST',
                                      value: EMaterskaSpol.ANO
                                  },
                                  /* ...(primaMatkaForm?.getFieldValue('stav') === EMaterskaSpol.NE &&
                                  primaMatkaForm?.getFieldValue('duvod') === 'NON_PUBLIC'
                                      ? [*/
                                  ...(typZadosti === ETypZadostiLei.GENERAL
                                      ? [
                                            {
                                                labelcz: 'LEI_MATKA_KONSOLIDOVANA_NEUVEST',
                                                value: EMaterskaSpol.NE
                                            }
                                        ]
                                      : []),

                                  /* ]
                                      : [])*/ ...leiMaMaterskouSpolFinalSelect
                              ]
                    }
                    disabled={disabled || typZadosti === 'SOLE_PROPRIETOR'}
                    onChange={val => {
                        if (val === EMaterskaSpol.ANO_SAME) {
                            // vytiahne aktualne hodnoty zprimej matky a nastavi ich do finalnej

                            form.setFieldsValue({
                                ...primaMatkaForm?.getFieldsValue(),
                                stav: EMaterskaSpol.ANO_SAME
                            });
                            // MHO - pomocná proměnná, abych věděl, kdy vyplnit 'Totozna' do stavu finální matky
                            setHideDefaultValues(false);
                            setSelectedMatkaSame(true);
                            primaMatkaFilelist && setFileList(primaMatkaFilelist);
                            primaMatkaPrilozeneSoubory && setPrilozeneSoubory(primaMatkaPrilozeneSoubory);
                            setTimeout(() => rerender(), 0);
                        } else {
                            setHideDefaultValues(true);
                            setSelectedMatkaSame(false);
                            setFileList([]);
                            setPrilozeneSoubory([]);
                        }
                        setErrorLei('');
                        setEntityCheckErr('');
                        rerender();
                    }}
                />
                {disabled && (
                    <>
                        <SelectComponent
                            isFormItem={true}
                            name="duvod"
                            disabled={disabled || selectedMatkaSame}
                            defaultValue={!hideDefaultValues && primaMatkaForm?.getFieldValue('duvod')}
                            data={
                                typZadosti === 'FUND'
                                    ? [
                                          {
                                              labelcz: 'NON_CONSOLIDATING',
                                              value: EMatNemaDuvod.NON_CONSOLIDATING
                                          }
                                      ]
                                    : leiMatNemaDuvodSelect
                            }
                            labelParagraph="LEI_DETAIL_DUVOD_SELECT"
                        />
                    </>
                )}
                {!disabled && (
                    <>
                        {(form.getFieldValue('stav') === EMaterskaSpol.ANO ||
                            (primaMatkaForm?.getFieldValue('stav') === EMaterskaSpol.ANO &&
                                form.getFieldValue('stav') === EMaterskaSpol.ANO_SAME)) && (
                            <>
                                <SelectComponent
                                    isFormItem={true}
                                    name="malei"
                                    disabled={
                                        selectedMatkaSame ||
                                        typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION ||
                                        typZadosti === ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY
                                    }
                                    defaultValue={
                                        typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION ||
                                        typZadosti === ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY
                                            ? 'ano'
                                            : !hideDefaultValues && isData
                                              ? isData[0].matkaslei
                                                  ? 'ano'
                                                  : isData[0].matkabezlei && 'ne'
                                              : undefined
                                    }
                                    labelParagraph="LEI_FINAL_MASPOL_MALEI"
                                    data={anoNe}
                                    onChange={() => {
                                        setErrorLei('');
                                        setEntityCheckErr('');
                                        rerender();
                                    }}
                                />
                                {(form.getFieldValue('malei') === EAnoNe.ANO ||
                                    typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION ||
                                    typZadosti === ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY) && (
                                    <LoadingWrapper loading={leiCheckLoading} loadingText="NACITANI_DAT">
                                        <InputComponent
                                            label="LEI_DETAIL_MATER_SPOLOCNOSTI_FIN"
                                            name="matkaslei"
                                            initialValue={!hideDefaultValues ? isData && isData[0].matkaslei : ''}
                                            disabled={selectedMatkaSame}
                                            rules={[
                                                ...leiValidation(async (value: string) => {
                                                    if (value !== lei?.detailData?.spolecnost?.legalentityidentifier) {
                                                        setLeiCheckLoading(true);
                                                        const resp = value
                                                            ? await checkLeiApi(value).catch(() => null)
                                                            : null;
                                                        setLeiCheckLoading(false);
                                                        form.setFieldsValue({
                                                            leinazev: resp ? resp.spolecnost : undefined
                                                        });
                                                        rerender();
                                                        if (
                                                            (typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION ||
                                                                typZadosti ===
                                                                    ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY) &&
                                                            resp?.kategorie &&
                                                            resp?.kategorie !== 'RESIDENT_GOVERNMENT_ENTITY'
                                                        ) {
                                                            return setLeiCheckError('CHYBNE_LEI2');
                                                        }
                                                        if (
                                                            resp?.stavlei === 'RETIRED' ||
                                                            resp?.stavlei === 'ANNULLED' ||
                                                            resp?.stavlei === 'DUPLICATE'
                                                        ) {
                                                            return setLeiCheckError('CHYBNE_LEI');
                                                        }
                                                        if (!resp && value) {
                                                            return setLeiCheckError('LEI_CHECK_ERROR');
                                                        }
                                                        return setLeiCheckError('');
                                                    }
                                                }),
                                                () => ({
                                                    validator(_, value: string) {
                                                        if (
                                                            value === lei?.detailData?.spolecnost?.legalentityidentifier
                                                        )
                                                            return Promise.reject(
                                                                <TranslateText>LEI_MATKA_STEJNE_ERR</TranslateText>
                                                            );
                                                        if (!value || !leiCheckError) return Promise.resolve();
                                                        return Promise.reject();
                                                    }
                                                })
                                            ]}
                                            maxLength={20}
                                            onChange={() => setLeiCheckError('')}
                                            validateTrigger={['onBlur', 'onSubmit']}
                                            validateStatus={leiCheckError ? 'error' : undefined}
                                            extramessage={leiCheckError}
                                        />
                                        {form.getFieldValue('leinazev') && (
                                            <InputComponent
                                                label="LEI_DETAIL_MATER_NAZEV_FINAL"
                                                name="leinazev"
                                                placeholder=""
                                                disabled={true}
                                                initialValue={''}
                                                maxLength={80}
                                                notRequired={true}
                                                rules={[]}
                                            />
                                        )}
                                    </LoadingWrapper>
                                )}
                                {form.getFieldValue('malei') === EAnoNe.NE && (
                                    <>
                                        <SelectComponent
                                            isFormItem={true}
                                            name="jurisdikce"
                                            defaultValue={
                                                !hideDefaultValues && finalniMatkaData && finalniMatkaData.length
                                                    ? finalniMatkaData[0].matkabezlei &&
                                                      finalniMatkaData[0].matkabezlei?.jurisdikce
                                                    : undefined
                                            }
                                            labelParagraph="LEI_DETAIL_JURISDICKE"
                                            data={cisStaty}
                                            disabled={selectedMatkaSame}
                                            onChange={() => {
                                                form.setFieldsValue({
                                                    registrationauthorityid: undefined
                                                });
                                                setShowDefaultValues(pv => {
                                                    return {
                                                        ...pv,
                                                        registrationauthorityid: false
                                                    };
                                                });

                                                // form.validateFields(['registrationauthorityentityid']);
                                            }}
                                        />

                                        <SelectComponent
                                            isFormItem={true}
                                            name="registrationauthorityid"
                                            defaultValue={
                                                !hideDefaultValues && showDefaultValues.registrationauthorityid
                                                    ? finalniMatkaData && finalniMatkaData.length
                                                        ? finalniMatkaData[0].matkabezlei &&
                                                          finalniMatkaData[0].matkabezlei?.registrationauthorityid
                                                        : undefined
                                                    : undefined
                                            }
                                            showOptionValue={true}
                                            optionValueFilter={['RA888888']}
                                            disabled={selectedMatkaSame}
                                            labelParagraph="LEI_DETAIL_REJSTRIK"
                                            data={[
                                                ...(form.getFieldValue('jurisdikce') !== undefined &&
                                                Object.keys(leiRejstrikySelect).includes(
                                                    form.getFieldValue('jurisdikce')
                                                )
                                                    ? _getKeyValue_(form.getFieldValue('jurisdikce'))(
                                                          leiRejstrikySelect
                                                      )
                                                    : []),
                                                ...leiRejstrikyPermanent
                                            ]}
                                            onChange={val => {
                                                val === 'RA000161' &&
                                                    form.setFieldsValue({
                                                        registrationauthorityentityid: 'ΗΕ'
                                                    });
                                                rerender(true);
                                            }}
                                        />
                                        {form?.getFieldValue('registrationauthorityid') === 'RA888888' && (
                                            <InputComponent
                                                label="LEI_REJSTRIK_INY"
                                                disabled={selectedMatkaSame}
                                                name="otherregistrationauthorityid"
                                                initialValue={
                                                    isData && isData[0].matkabezlei?.otherregistrationauthorityid
                                                }
                                                maxLength={80}
                                            />
                                        )}

                                        <InputComponent
                                            label="LEI_FINAL_ID_KONSOL"
                                            name="registrationauthorityentityid"
                                            disabled={selectedMatkaSame}
                                            initialValue={
                                                !hideDefaultValues
                                                    ? isData && isData[0].matkabezlei?.registrationauthorityentityid
                                                    : ''
                                            }
                                            rules={leiRejstrikValidation(form.getFieldValue('registrationauthorityid'))}
                                            maxLength={30}
                                        />
                                    </>
                                )}
                                {form.getFieldValue('malei') && form.getFieldValue('malei') !== EAnoNe.NE && (
                                    <>
                                        <DatePickerComponent
                                            disabled={selectedMatkaSame}
                                            label="LEI_DETAIL_MATKA_DATUM"
                                            name="relationship"
                                            disabledDate={curr => curr > dayjs()}
                                            initialValue={
                                                !hideDefaultValues
                                                    ? finalniMatkaDates && finalniMatkaDates.relationship
                                                        ? finalniMatkaDates.relationship.startdate &&
                                                          dayjs(finalniMatkaDates.relationship.startdate)
                                                        : undefined
                                                    : undefined
                                            }
                                        />
                                        <InputNumberComponent
                                            disabled={selectedMatkaSame}
                                            label="LEI_DETAIL_MATKA_PODIL"
                                            name="podilspolecnosti"
                                            initialValue={
                                                !hideDefaultValues
                                                    ? isData && isData[0].informacekmatce?.podilspolecnosti
                                                    : undefined
                                            }
                                            rules={procentaValidation()}
                                            maxLength={8}
                                            //    formInstance={form}
                                        />
                                        <SelectComponent
                                            isFormItem={true}
                                            disabled={selectedMatkaSame}
                                            name="konsolidace"
                                            defaultValue={
                                                !hideDefaultValues
                                                    ? isData && isData[0].informacekmatce?.konsolidace
                                                    : undefined
                                            }
                                            labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE"
                                            data={
                                                typZadosti === ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY
                                                    ? leiKonsolidaceTypSelectAltGov
                                                    : typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                                                      ? leiKonsolidaceTypSelectAlt
                                                      : leiKonsolidaceTypSelect
                                            }
                                        />
                                        <Form.Item
                                            name="soubor"
                                            label={
                                                <Paragraph
                                                    text={language === 'en' ? 'LEI_DETAIL_UPLOAD' : 'SOUBOR'}
                                                    strong={true}
                                                />
                                            }
                                            rules={[
                                                {
                                                    required: true,
                                                    validator() {
                                                        try {
                                                            if (!fileList || fileList.length < 1)
                                                                return Promise.reject(
                                                                    <TranslateText>
                                                                        NEVALIDNI_POVINNA_POLOZKA_UPLOAD
                                                                    </TranslateText>
                                                                );
                                                            return Promise.resolve();
                                                        } catch {
                                                            return Promise.reject();
                                                        }
                                                    }
                                                }
                                            ]}
                                        >
                                            <div
                                                style={{
                                                    marginBottom: 22
                                                }}
                                            >
                                                {language !== 'en' && (
                                                    <p>
                                                        <TranslateText>LEI_VLOZTE_MAT</TranslateText>
                                                    </p>
                                                )}
                                                <UploadComponent
                                                    {...uploadProps}
                                                    disabled={selectedMatkaSame}
                                                    accept={'.pdf, .rtf, .docx, .doc'}
                                                    id="finalMatkaUploadBtn"
                                                />
                                            </div>
                                        </Form.Item>
                                        {(fileList || []).length > 0 && (
                                            <>
                                                {' '}
                                                <SelectComponent
                                                    disabled={selectedMatkaSame}
                                                    isFormItem={true}
                                                    name="validationdocuments"
                                                    defaultValue={
                                                        !hideDefaultValues
                                                            ? isData && isData[0].informacekmatce?.validationdocuments
                                                            : undefined
                                                    }
                                                    labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE_CONFIRM"
                                                    data={leiKonsolidaceSouborySelect}
                                                    onChange={() => rerender()}
                                                />
                                                {form.getFieldValue('validationdocuments') ===
                                                    ETypDatMatky.REGULATORY_FILING && (
                                                    <InputComponent
                                                        disabled={selectedMatkaSame}
                                                        label="LEI_DETAIL_REGULATORY"
                                                        name="validationreference"
                                                        initialValue={
                                                            !hideDefaultValues
                                                                ? isData &&
                                                                  isData[0].informacekmatce?.validationreference
                                                                : ''
                                                        }
                                                        onChange={() => rerender()}
                                                        maxLength={100}
                                                    />
                                                )}
                                                {form.getFieldValue('validationdocuments') && (
                                                    <>
                                                        <DatePickerComponent
                                                            disabledDate={current =>
                                                                form.getFieldValue('accountingend') &&
                                                                current >
                                                                    dayjs(form.getFieldValue('accountingend')).subtract(
                                                                        1,
                                                                        'days'
                                                                    )
                                                            }
                                                            label="LEI_DETAIL_UCETNI_OBDOBY_OD"
                                                            name="accountingstart"
                                                            initialValue={
                                                                !hideDefaultValues &&
                                                                finalniMatkaDates &&
                                                                finalniMatkaDates.accounting
                                                                    ? dayjs(finalniMatkaDates.accounting.startdate)
                                                                    : undefined
                                                            }
                                                            disabled={selectedMatkaSame}
                                                        />
                                                        <DatePickerComponent
                                                            disabledDate={current =>
                                                                form.getFieldValue('accountingstart') &&
                                                                current <
                                                                    dayjs(form.getFieldValue('accountingstart')).add(
                                                                        1,
                                                                        'days'
                                                                    )
                                                            }
                                                            label="LEI_DETAIL_UCETNI_OBDOBY_DO"
                                                            name="accountingend"
                                                            initialValue={
                                                                !hideDefaultValues &&
                                                                finalniMatkaDates &&
                                                                finalniMatkaDates.accounting
                                                                    ? finalniMatkaDates.accounting.enddate &&
                                                                      dayjs(finalniMatkaDates.accounting.enddate)
                                                                    : undefined
                                                            }
                                                            notRequired={
                                                                form.getFieldValue('validationdocuments') !==
                                                                    ETypDatMatky.ACCOUNTS_FILING &&
                                                                form.getFieldValue('validationdocuments') !==
                                                                    ETypDatMatky.REGULATORY_FILING
                                                            }
                                                            disabled={selectedMatkaSame}
                                                        />{' '}
                                                    </>
                                                )}
                                                {form.getFieldValue('validationdocuments') &&
                                                    form.getFieldValue('validationdocuments') !==
                                                        ETypDatMatky.ACCOUNTS_FILING && (
                                                        <>
                                                            <DatePickerComponent
                                                                disabledDate={current =>
                                                                    form.getFieldValue('documentend') &&
                                                                    current >
                                                                        dayjs(
                                                                            form.getFieldValue('documentend')
                                                                        ).subtract(1, 'days')
                                                                }
                                                                label="LEI_DETAIL_PLATNOSTDOKUMENTU_OD"
                                                                name="documentstart"
                                                                initialValue={
                                                                    !hideDefaultValues &&
                                                                    finalniMatkaDates &&
                                                                    finalniMatkaDates.document
                                                                        ? dayjs(finalniMatkaDates.document.startdate)
                                                                        : undefined
                                                                }
                                                                disabled={selectedMatkaSame}
                                                            />
                                                            <DatePickerComponent
                                                                disabledDate={current =>
                                                                    form.getFieldValue('documentstart') &&
                                                                    current <
                                                                        dayjs(form.getFieldValue('documentstart')).add(
                                                                            1,
                                                                            'days'
                                                                        )
                                                                }
                                                                label="LEI_DETAIL_PLATNOSTDOKUMENTU_DO"
                                                                name="documentend"
                                                                initialValue={
                                                                    !hideDefaultValues &&
                                                                    finalniMatkaDates &&
                                                                    finalniMatkaDates.document?.enddate
                                                                        ? dayjs(finalniMatkaDates.document.enddate)
                                                                        : undefined
                                                                }
                                                                notRequired={true}
                                                                disabled={selectedMatkaSame}
                                                            />
                                                        </>
                                                    )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {(form.getFieldValue('stav') === EMaterskaSpol.NELZE_UVEST ||
                            (primaMatkaForm?.getFieldValue('stav') === EMaterskaSpol.NELZE_UVEST &&
                                form.getFieldValue('stav') === EMaterskaSpol.ANO_SAME)) && (
                            <>
                                <SelectComponent
                                    isFormItem={true}
                                    name="duvod"
                                    labelParagraph="LEI_DETAIL_DUVOD_SELECT"
                                    data={leiMatNelzeDuvodSelect}
                                    defaultValue={!hideDefaultValues && isData ? isData[0].duvod : undefined}
                                    disabled={selectedMatkaSame}
                                />
                                <InputTextArea
                                    rows={4}
                                    name="exceptionreference"
                                    initialValue={
                                        !hideDefaultValues ? isData && isData[0].exceptionreference : undefined
                                    }
                                    label="LEI_DETAIL_NELZEUVEST_INPUT"
                                    maxLength={1000}
                                    disabled={selectedMatkaSame}
                                />
                            </>
                        )}
                        {(form.getFieldValue('stav') === EMaterskaSpol.NE ||
                            (primaMatkaForm?.getFieldValue('stav') === EMaterskaSpol.NE &&
                                form.getFieldValue('stav') === EMaterskaSpol.ANO_SAME)) && (
                            <>
                                <SelectComponent
                                    isFormItem={true}
                                    disabled={selectedMatkaSame}
                                    name="duvod"
                                    // defaultValue={!hideDefaultValues && primaMatkaForm?.getFieldValue('duvod')}
                                    defaultValue={
                                        (!hideDefaultValues && isData && isData[0].duvod === 'NON_PUBLIC') ||
                                        form.getFieldValue('stav') !== EMaterskaSpol.ANO_SAME
                                            ? isData
                                                ? isData[0].duvod
                                                : undefined
                                            : primaMatkaForm?.getFieldValue('duvod') || undefined
                                    }
                                    labelParagraph="LEI_DETAIL_DUVOD_SELECT"
                                    data={[
                                        {
                                            labelcz: 'NON_PUBLIC',
                                            value: EMatNemaDuvod.NON_PUBLIC
                                        }
                                    ]}
                                    onChange={() => rerender()}
                                />
                            </>
                        )}
                    </>
                )}
                <Row className="button-row">
                    <ButtonComponent
                        onClick={
                            () => {
                                setErrorLei('');
                                setEntityCheckErr('');
                                if (showFinalniMatka && setShowFinalniMatka) {
                                    setShowFinalniMatka(false);
                                } else {
                                    prevStep();
                                }
                            }
                            /* primaMatkaForm?.getFieldValue('malei') === EAnoNe.NE
                                ? setShowFinalniMatka && setShowFinalniMatka(false)
                                : prevStep()*/
                        }
                        id="finalMatZpetBtn"
                        buttontext="ZPET"
                        type="default"
                    />

                    <ButtonComponent
                        id="finalMatSubmitBtn"
                        type="primary"
                        htmlType="button"
                        onClick={() => {
                            disabled ? primaMatkaForm?.submit() : form.submit();
                        }}
                        extramessage={
                            entityCheckErr === 'existuje'
                                ? ((
                                      <TranslateText params={[errorLei ? ` - ${errorLei}` : '']}>
                                          LEI_ENTITYCHECK_ERR
                                      </TranslateText>
                                  ) as unknown as string)
                                : entityCheckErr === 'shodneico'
                                  ? 'LEI_ENTITYSHODNE_ERR'
                                  : ''
                        }
                        buttontext="POKRACOVAT"
                        loading={loading || uploading}
                    />
                </Row>
            </FormWrapper>
        </>
    );
};

export default FinalniMatka;
